import { useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import time from 'utils/time';
import DescriptionTinyEditor from 'components/Dashboard/UserContent/ui/DescriptionTinyEditor';
import { CalendarPopup } from 'components/Dashboard/Filters/ui/DateFilter';
import DropdownComponent from 'components/Settings/WebsiteFeeds/DropdownComponent';
import { store } from 'components/Store';
import PeopleAsyncSelect from './PeopleAsyncSelect';
import MembersSelect from './MembersSelect';

export const INTERACTION_TYPES = ['Email', 'Meeting', 'Phone call', 'Social media', 'Events', 'Other'];

const RemoveSelectionButton = ({ id, count, singular, plural, onRemoveAll, openDropdown, setOpenDropdown }) => {
  if (!count) return null;
  const isOpen = openDropdown === id;
  return (
    <div className='position-relative d-inline-block flex-shrink-0'>
      <button
        type='button'
        className='general-button circle-button hover-background-gray-05 p-1'
        onClick={() => setOpenDropdown((d) => (d === id ? null : id))}
      >
        <span className='icon-three-dots text-main-blue-40' />
      </button>
      {isOpen && (
        <>
          <div className='general-close-background z-max' onClick={() => setOpenDropdown(null)} />
          <div className='crm-actions-box box box-shadow specific-top-padding-6 specific-bottom-padding-6'>
            <button
              type='button'
              className='general-button specific-right-padding-35 specific-left-padding-16 d-flex align-items-center list-action-button'
              onClick={() => {
                onRemoveAll();
                setOpenDropdown(null);
              }}
            >
              <span className='icon-remove-stakeholders text-dark-red' />
              <span className='specific-left-padding-14 text-dark-red'>
                {count === 1 ? `Remove this ${singular}` : `Remove all ${count} ${plural}`}
              </span>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const InteractionFormFields = ({ form, onChange }) => {
  const { state } = useContext(store);
  const teamName = state.team?.teamName ?? 'Organisation';
  const organisationName = state.activePlan?.organisationName ?? teamName;

  const [calendarState, setCalendarState] = useState({
    active: false,
    filterProperty: '',
    selectedDay: form.date ? time.parseCalendarDateString(form.date).toDate() : new Date(),
    name: '',
    range: { from: '', to: '' },
  });

  const [openDropdown, setOpenDropdown] = useState(null);
  const closeSignal = (id) => (openDropdown !== id ? openDropdown : null);
  const onMenuCloseFor = (id) => () => setOpenDropdown((d) => (d === id ? null : d));

  const openCalendar = () => {
    setOpenDropdown('date');
    setCalendarState((s) => ({
      ...s,
      active: true,
      selectedDay: form.date ? time.parseCalendarDateString(form.date).toDate() : new Date(),
    }));
  };

  useEffect(() => {
    if (openDropdown !== 'date') setCalendarState((s) => (s.active ? { ...s, active: false } : s));
  }, [openDropdown]);

  const initiatorLabel =
    form.initiator === 'Organisation' ? teamName : form.initiator === 'Stakeholder' ? 'Stakeholder' : null;

  return (
    <>
      <div className='row mx-0 mb-3'>
        <div className='col pr-2 pl-0' style={{ minWidth: 0 }}>
          <label className='d-block mb-1 paragraph-p1 font-weight-bold'>Title</label>
          <input
            type='text'
            className='form-control border no-focus-outline'
            value={form.title}
            onChange={(e) => onChange('title', e.target.value)}
          />
        </div>
        <div className='col pl-2 pr-0' style={{ minWidth: 0 }}>
          <label className='d-block mb-1 paragraph-p1 font-weight-bold'>Date</label>
          <button
            type='button'
            className='form-control border text-left no-focus-outline d-flex align-items-center justify-content-between'
            onClick={openCalendar}
          >
            <span>{form.date ? dayjs(form.date).format('D MMMM YYYY') : 'Select date'}</span>
            <span className='icon-calendar' />
          </button>
          <CalendarPopup
            showPopup={calendarState.active}
            calendarState={calendarState}
            setCalendarState={setCalendarState}
            ApplyCalendarFilters={() => onChange('date', dayjs(calendarState.selectedDay).format('YYYY-MM-DD'))}
            setShowPopup={(val) => setCalendarState((s) => ({ ...s, active: val }))}
            noFutureDatesAllowed={false}
            selectedMonth={calendarState.selectedDay ? dayjs(calendarState.selectedDay).toDate() : new Date()}
          />
        </div>
      </div>

      <div className='row mx-0 mb-3'>
        <div className='col pr-2 pl-0 shadow-dropdown' style={{ minWidth: 0 }}>
          <label className='d-block mb-1 paragraph-p1 font-weight-bold'>Type</label>
          <DropdownComponent
            mainItem={form.type || 'Select type'}
            fixedDropdown
            mainItemClass='form-control border no-focus-outline'
            dropdownClass='shadow-open-dropdown interaction-form-dropdown'
            fixedTopSpace={8}
            onOpen={() => setOpenDropdown('type')}
            closeSignal={closeSignal('type')}
          >
            <ul className='policy-list p-0 m-0' style={{ overflowY: 'auto', maxHeight: '200px' }}>
              {INTERACTION_TYPES.map((t) => (
                <li
                  key={t}
                  onClick={() => onChange('type', t)}
                  className='list-action-button py-2 px-3 flex-centered pointer'
                >
                  <span className='flex-grow-1'>{t}</span>
                  {form.type === t && <span className='icon-tick paragraph-p1' />}
                </li>
              ))}
            </ul>
          </DropdownComponent>
        </div>
        <div className='col pl-2 pr-0 shadow-dropdown' style={{ minWidth: 0 }}>
          <label className='d-block mb-1 paragraph-p1 font-weight-bold'>Initiator</label>
          <DropdownComponent
            mainItem={initiatorLabel || 'Select initiator'}
            fixedDropdown
            mainItemClass='form-control border no-focus-outline'
            dropdownClass='shadow-open-dropdown interaction-form-dropdown'
            fixedTopSpace={8}
            onOpen={() => setOpenDropdown('initiator')}
            closeSignal={closeSignal('initiator')}
          >
            <ul className='policy-list p-0 m-0'>
              {[
                { value: 'Organisation', label: teamName },
                { value: 'Stakeholder', label: 'Stakeholder' },
              ].map(({ value, label }) => (
                <li
                  key={value}
                  onClick={() => onChange('initiator', value)}
                  className='list-action-button py-2 px-3 flex-centered pointer'
                >
                  <span className='flex-grow-1'>{label}</span>
                  {form.initiator === value && <span className='icon-tick paragraph-p1' />}
                </li>
              ))}
            </ul>
          </DropdownComponent>
        </div>
      </div>

      <div className='row mx-0 mb-3'>
        <div className='col pr-2 pl-0 d-flex flex-column' style={{ minWidth: 0 }}>
          <div className='d-flex justify-content-between align-items-center mb-1'>
            <label className='paragraph-p1 font-weight-bold mb-0 text-truncate' style={{ minWidth: 0 }}>
              People from {organisationName}
            </label>
            <RemoveSelectionButton
              id='members-menu'
              count={form.members?.length ?? 0}
              singular='person'
              plural='people'
              onRemoveAll={() => onChange('members', [])}
              openDropdown={openDropdown}
              setOpenDropdown={setOpenDropdown}
            />
          </div>
          <MembersSelect
            value={form.members ?? []}
            onChange={(selected) => onChange('members', selected)}
            placeholder='Add person'
            menuIsOpen={openDropdown === 'members'}
            onMenuOpen={() => setOpenDropdown('members')}
            onMenuClose={onMenuCloseFor('members')}
          />
        </div>
        <div className='col pl-2 pr-0 d-flex flex-column' style={{ minWidth: 0 }}>
          <div className='d-flex justify-content-between align-items-center mb-1'>
            <label className='paragraph-p1 font-weight-bold mb-0'>Stakeholders</label>
            <RemoveSelectionButton
              id='stakeholders-menu'
              count={form.stakeholders?.length ?? 0}
              singular='stakeholder'
              plural='stakeholders'
              onRemoveAll={() => onChange('stakeholders', [])}
              openDropdown={openDropdown}
              setOpenDropdown={setOpenDropdown}
            />
          </div>
          <PeopleAsyncSelect
            value={form.stakeholders ?? []}
            onChange={(selected) => onChange('stakeholders', selected ?? [])}
            placeholder='Add stakeholder'
            menuIsOpen={openDropdown === 'stakeholders'}
            onMenuOpen={() => setOpenDropdown('stakeholders')}
            onMenuClose={onMenuCloseFor('stakeholders')}
          />
        </div>
      </div>

      <div className='mb-3'>
        <label className='d-block mb-2 paragraph-p2 font-weight-bold'>Description</label>
        <div className='influence-description interaction-form-description w-100' style={{ display: 'flow-root' }}>
          <DescriptionTinyEditor
            value={form.description}
            onChangeFunction={(content) => onChange('description', content)}
            minHeight={200}
            maxHeight={300}
            hideSkeleton
            autoresize_bottom_margin={14}
            disableAutofocus
            toolbarLocation='bottom'
            colorPickerPosition='top'
          />
        </div>
      </div>
    </>
  );
};

export default InteractionFormFields;
